import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FONT_SIZE, LEDGER } from "../../../theme/tokens";

/**
 * - `item` — a line of the sum (a segment, a charge): primary weight, figures in both columns.
 * - `component` — part of the item above it (a rule that built its rate): smaller, muted, indented and
 *   hung off a rail so a run of them reads as one item's breakdown.
 * - `deduction` — something taken out rather than added (an unpaid break): muted, at item level.
 * - `adjustment` — a line that exists only to make the item figures reconcile (rounding): small, muted,
 *   italic, at item level. A reconciling line INSIDE an item's breakdown is a `component` with an italic label.
 */
export type LedgerLineVariant = "item" | "component" | "deduction" | "adjustment";

/** Lines set a notch under the item size. */
const isSmall = (variant: LedgerLineVariant) => variant !== "item";

/**
 * The line's grid: glyph slot | label + detail | rate | amount. The figure columns are fixed-width tokens,
 * so every line of a list shares them. A component line is indented and draws the rail — on the whole line,
 * padding included, so a run of component lines joins into one continuous guide.
 */
export const ledgerLineSx =
	(variant: LedgerLineVariant): SxProps<Theme> =>
	(theme) => {
		const componentLike = variant === "component";
		return {
			position: "relative",
			display: "grid",
			gridTemplateColumns: `${componentLike ? LEDGER.COMPONENT_INDENT : LEDGER.LEADING_WIDTH} minmax(0, 1fr) ${LEDGER.RATE_COLUMN_WIDTH} ${LEDGER.AMOUNT_COLUMN_WIDTH}`,
			columnGap: 1.5,
			alignItems: "baseline",
			// Padding, not margin: consecutive component lines must touch for the rail to read as one.
			py: componentLike ? 0.375 : 0.75,
			m: 0,
			listStyle: "none",
			fontSize: isSmall(variant) ? FONT_SIZE.SECONDARY : FONT_SIZE.EMPHASIS,
			color: variant === "item" ? theme.palette.text.primary : theme.palette.text.secondary,
			fontVariantNumeric: "tabular-nums",
			...(componentLike && {
				"&::before": {
					content: '""',
					position: "absolute",
					// Centred under the parent item's glyph slot, so the rail visibly hangs from it.
					insetInlineStart: `calc(${LEDGER.LEADING_WIDTH} / 2)`,
					top: 0,
					bottom: 0,
					width: LEDGER.RAIL_WIDTH,
					marginInlineStart: `${-LEDGER.RAIL_WIDTH / 2}px`,
					backgroundColor: alpha(theme.palette.primary.main, LEDGER.RAIL_ALPHA),
				},
			}),
		};
	};

export const LEDGER_LEADING_SX = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	alignSelf: "center",
	"& > svg": { fontSize: FONT_SIZE.TITLE_SM },
} as const;

/**
 * `break-word`, not `anywhere`: both keep an overlong unbreakable string (a long rule name) from overflowing,
 * but `break-word` only ever breaks inside a word that cannot fit a line on its own — so a detail's
 * no-break-space-bound time range wraps whole, at the " · " before it.
 */
export const LEDGER_BODY_SX = { minWidth: 0, overflowWrap: "break-word" } as const;

/**
 * Lines at item level (an item, a deduction) share a label column, so their details start on one edge and
 * need no "·" to set them off; component lines hang off the rail and keep it.
 */
export const sharesLabelColumn = (variant: LedgerLineVariant) => variant === "item" || variant === "deduction";

export const ledgerLabelSx = (variant: LedgerLineVariant): SxProps<Theme> => ({
	// The right padding keeps a name longer than the column off the detail that follows it.
	// No wrap inside the column: a name split over two lines would put its details out of line with the rest.
	...(sharesLabelColumn(variant) && { display: "inline-block", minWidth: LEDGER.ITEM_LABEL_MIN_WIDTH, pr: 1.5, whiteSpace: "nowrap" }),
	fontWeight: variant === "item" ? 600 : variant === "deduction" ? 500 : 400,
	fontStyle: variant === "adjustment" ? "italic" : undefined,
	color: variant === "item" ? "text.primary" : undefined,
});

/** An item's figures are both set bold, rate and amount alike: they are what the reader scans the list for. */
export const ledgerRateSx = (variant: LedgerLineVariant, figureColor: string | undefined): SxProps<Theme> => ({
	textAlign: "end",
	whiteSpace: "nowrap",
	fontWeight: variant === "item" ? 600 : 400,
	color: figureColor ?? (variant === "item" ? "text.secondary" : undefined),
});

export const LEDGER_UNIT_SX = { display: "inline-block", width: LEDGER.RATE_UNIT_WIDTH, textAlign: "start", color: "text.secondary" } as const;

export const ledgerAmountSx = (variant: LedgerLineVariant, figureColor: string | undefined): SxProps<Theme> => ({
	textAlign: "end",
	whiteSpace: "nowrap",
	fontWeight: variant === "item" ? 600 : 400,
	color: figureColor,
});
